import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Berhasil masuk");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error("Gagal masuk", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-navy px-6 text-sand-50" data-testid="admin-login-page">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-[26rem] w-[26rem] rounded-full bg-azure/50 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-[22rem] w-[22rem] rounded-full bg-gold/10 blur-3xl" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md rounded-[2rem] border border-white/10 bg-white/5 p-8 backdrop-blur-xl sm:p-10"
      >
        <Logo className="h-16 w-16 ring-2 ring-gold/40" />
        <p className="eyebrow mt-6 text-gold">Panel Pengurus</p>
        <h1 className="mt-2 font-display text-4xl leading-tight">
          Kelola <em className="text-gold">Pesan</em> & Agenda
        </h1>
        <p className="mt-3 text-sm text-sand-50/60">Masuk untuk membaca pesan jamaah dan mengatur agenda kegiatan masjid.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <label className="block">
            <span className="eyebrow text-sand-50/60">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@masjidpantaibali.id"
              className="mt-2 w-full rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-sm text-sand-50 placeholder:text-sand-50/40 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-4 focus:ring-gold/10"
              data-testid="admin-email-input"
            />
          </label>
          <label className="block">
            <span className="eyebrow text-sand-50/60">Kata sandi</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 w-full rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-sm text-sand-50 placeholder:text-sand-50/40 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-4 focus:ring-gold/10"
              data-testid="admin-password-input"
            />
          </label>
          <button type="submit" disabled={busy} className="btn-accent w-full disabled:cursor-wait disabled:opacity-70" data-testid="admin-login-submit">
            {busy ? "Memproses…" : "Masuk"} <LogIn className="h-4 w-4" />
          </button>
        </form>
        <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-widest text-sand-50/40">
          <span className="h-px flex-1 bg-sand-50/15" /> atau <span className="h-px flex-1 bg-sand-50/15" />
        </div>
        <button
          type="button"
          onClick={googleLogin}
          className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-sand-50 px-7 py-4 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0"
          data-testid="admin-google-login"
        >
          <GoogleIcon /> Masuk dengan Google
        </button>
        <a href="/" className="mt-6 inline-block text-xs text-sand-50/50 transition-colors hover:text-gold" data-testid="admin-back-home">
          ← Kembali ke beranda
        </a>
      </motion.div>
    </div>
  );
}
